/*
 * Fix Alignment — state machine.
 *
 * read geometry → POST /alignment/fix → write the corrections back, keeping the
 * pre-fix geometry so the result can be reverted (Office.js geometry writes are
 * not reliably captured by PowerPoint's own undo stack).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import {
  ALIGNMENT_REQUEST_TIMEOUT_MS,
  describeAlignmentFailure,
  getErrorMessage,
  isAbortError,
  logFixAlignmentError,
  postFixAlignment,
  type AlignmentMode,
} from "../services/fixAlignmentApi";
import {
  applyGeometryUpdates,
  readSlideGeometry,
  toRevertUpdates,
  type AlignmentScope,
  type GeometryUpdate,
  type SlideSnapshot,
} from "../services/slideGeometry";

/** The stage labels shown while a fix runs — mirrors the mockup's progress steps. */
export type FixAlignmentPhase = "reading" | "analysing" | "applying";

export type FixAlignmentState =
  | { status: "idle" }
  | { status: "running"; phase: FixAlignmentPhase }
  /** The API had nothing to correct — the layout is already sound. */
  | { status: "no-changes" }
  /** The user stopped the run. Not a failure, so it is not styled as one. */
  | { status: "cancelled" }
  | { status: "done"; adjusted: number; mode: AlignmentMode; canRevert: boolean }
  | { status: "reverted"; adjusted: number }
  | { status: "error"; message: string };

const FIX_FAILED_MESSAGE = "Fix alignment failed";
const REVERT_FAILED_MESSAGE = "Couldn't put the shapes back — undo in PowerPoint instead.";
/**
 * Corrections came back but matched nothing on the slide — the user changed
 * slide or selection while the request was in flight, so every id was stale.
 * Distinct from "no-changes", which means the layout needed no fixing.
 */
const STALE_TARGET_MESSAGE =
  "The slide moved on while the fix was running, so nothing was applied. Try again.";

export function useFixAlignment() {
  const http = useDeckorateHttp();
  const [state, setState] = useState<FixAlignmentState>({ status: "idle" });
  const [scope, setScope] = useState<AlignmentScope>("all");

  // Guards a second run starting while one is in flight, matching
  // useSpeakerNotes/useBrandApply's double-click guard.
  const isRunningRef = useRef(false);
  // The pre-fix geometry of the shapes that were corrected, plus the scope they
  // were read under, so the revert targets the same shapes.
  const revertRef = useRef<{ scope: AlignmentScope; updates: GeometryUpdate[] } | null>(null);
  // Cancels the in-flight request on timeout, on Cancel, or on unmount.
  const abortRef = useRef<AbortController | null>(null);
  // Distinguishes "our deadline elapsed" from "the user walked away", since both
  // surface as an AbortError but deserve different copy.
  const timedOutRef = useRef(false);
  // Set on unmount so a late resolution doesn't call setState on a dead component.
  const isMountedRef = useRef(true);

  const safeSetState = useCallback((next: FixAlignmentState) => {
    if (isMountedRef.current) setState(next);
  }, []);

  // Abandon any in-flight request when the panel closes — without this the
  // fetch outlives the component and its result is applied to the deck after
  // the user has navigated away.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  const run = useCallback(async () => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;
    revertRef.current = null;
    timedOutRef.current = false;
    safeSetState({ status: "running", phase: "reading" });

    const controller = new AbortController();
    abortRef.current = controller;
    const timeoutId = setTimeout(() => {
      timedOutRef.current = true;
      controller.abort();
    }, ALIGNMENT_REQUEST_TIMEOUT_MS);

    try {
      const snapshot: SlideSnapshot = await readSlideGeometry(scope);

      safeSetState({ status: "running", phase: "analysing" });
      const result = await postFixAlignment(http, snapshot, scope, { signal: controller.signal });

      if (result.corrections.length === 0) {
        safeSetState({ status: "no-changes" });
        return;
      }

      safeSetState({ status: "running", phase: "applying" });
      const revertUpdates = toRevertUpdates(
        snapshot,
        result.corrections.map((correction) => correction.id),
      );
      const adjusted = await applyGeometryUpdates(scope, result.corrections);

      if (adjusted === 0) {
        logFixAlignmentError("fix-alignment-apply", STALE_TARGET_MESSAGE);
        safeSetState({ status: "error", message: STALE_TARGET_MESSAGE });
        return;
      }
      revertRef.current = { scope, updates: revertUpdates };
      safeSetState({ status: "done", adjusted, mode: result.mode, canRevert: true });
    } catch (error) {
      const timedOut = timedOutRef.current;
      // A user-initiated cancel is an expected outcome, not a failure: it is
      // neither reported to telemetry nor shown as an error. A timeout IS a
      // failure (it points at a slow backend), so it takes the error path.
      if (isAbortError(error) && !timedOut) {
        safeSetState({ status: "cancelled" });
        return;
      }
      // Log the raw failure (status code and envelope intact) but show the
      // reader-friendly version — a geometry error passes through unchanged.
      logFixAlignmentError("fix-alignment-run", getErrorMessage(error, FIX_FAILED_MESSAGE));
      safeSetState({
        status: "error",
        message: describeAlignmentFailure(error, FIX_FAILED_MESSAGE, { timedOut }),
      });
    } finally {
      clearTimeout(timeoutId);
      if (abortRef.current === controller) abortRef.current = null;
      isRunningRef.current = false;
    }
  }, [http, scope, safeSetState]);

  /** Abandon an in-flight fix. Nothing has been written to the deck yet. */
  const cancel = useCallback(() => {
    timedOutRef.current = false;
    abortRef.current?.abort();
  }, []);

  const revert = useCallback(async () => {
    const pending = revertRef.current;
    if (!pending) return;
    try {
      const restored = await applyGeometryUpdates(pending.scope, pending.updates);
      revertRef.current = null;
      safeSetState({ status: "reverted", adjusted: restored });
    } catch (error) {
      const message = getErrorMessage(error, REVERT_FAILED_MESSAGE);
      logFixAlignmentError("fix-alignment-revert", message);
      safeSetState({ status: "error", message });
    }
  }, [safeSetState]);

  /** Changing scope invalidates a stale result (and its revert) for the old scope. */
  const changeScope = useCallback((next: AlignmentScope) => {
    setScope(next);
    revertRef.current = null;
    setState({ status: "idle" });
  }, []);

  return { state, scope, setScope: changeScope, run, cancel, revert };
}
